import React, { useState, useEffect, useRef, useCallback } from 'react';
import theme from '../styles/theme';
import { api } from '../services/api';
import sagittalMriCache from '../services/sagittalMriCache';
import API_URL from '../config/api';

// For debugging only
const DEBUG = true;

// Component-scoped styles for the viewer chrome (slider thumb/track, HUD
// positioning, control bar). Kept identical in AxialMRIViewer.jsx so the
// two panes look and feel consistent. The 16px thumb (radius 8) matches the
// axial viewer's thumbRadius=8 marker-compensation math — do not change one
// without the other.
const viewerCss = `
.mri-viewer {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.mri-viewer--center {
  align-items: center;
  justify-content: center;
}
.mri-viewer__canvas {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mri-image-wrap {
  position: relative;
  max-width: 100%;
  max-height: 100%;
}
.mri-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.mri-hud {
  display: flex;
  align-items: center;
  gap: 8px;
  pointer-events: none;
  white-space: nowrap;
}
.mri-hud--tl { top: 10px; left: 10px; }
.mri-hud--tr { top: 10px; right: 10px; }
.mri-hud__value {
  font-family: var(--font-mono);
  font-size: 0.78rem;
  color: var(--text-primary);
}
.mri-viewer__controls {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  padding: 10px 14px;
  background: rgba(10, 12, 15, 0.72);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-top: 1px solid var(--line-subtle);
  z-index: 10;
}
.mri-nav-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: var(--radius-full);
  font-size: 0.95rem;
  flex: none;
}
.mri-slider-wrap {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  max-width: 520px;
  display: flex;
  align-items: center;
}
.mri-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 16px;
  margin: 0;
  background: transparent;
  cursor: pointer;
}
.mri-slider:disabled {
  cursor: default;
  opacity: 0.45;
}
.mri-slider:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
  border-radius: var(--radius-full);
}
.mri-slider::-webkit-slider-runnable-track {
  height: 4px;
  border-radius: var(--radius-full);
  background: rgba(255, 255, 255, 0.14);
}
.mri-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  margin-top: -6px;
  border-radius: var(--radius-full);
  background: var(--accent);
  border: 2px solid rgba(255, 255, 255, 0.92);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
  transition: transform var(--dur-fast) var(--ease-spring);
}
.mri-slider:hover:not(:disabled)::-webkit-slider-thumb { transform: scale(1.12); }
.mri-slider:active:not(:disabled)::-webkit-slider-thumb { transform: scale(0.96); }
.mri-slider::-moz-range-track {
  height: 4px;
  border-radius: var(--radius-full);
  background: rgba(255, 255, 255, 0.14);
}
.mri-slider::-moz-range-progress {
  height: 4px;
  border-radius: var(--radius-full);
  background: var(--accent);
}
.mri-slider::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border-radius: var(--radius-full);
  background: var(--accent);
  border: 2px solid rgba(255, 255, 255, 0.92);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
}
.mri-slider-markers {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.mri-marker {
  position: absolute;
  top: 50%;
  margin-top: -4px;
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
  border: 1px solid rgba(0, 0, 0, 0.55);
  transition: transform var(--dur-fast) var(--ease-spring),
    background-color var(--dur-fast) ease,
    box-shadow var(--dur-fast) ease;
}
.mri-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgba(5, 6, 8, 0.72);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  color: var(--text-secondary);
  font-size: 0.86rem;
  text-align: center;
}
.mri-overlay p { margin: 0; }
.mri-overlay__progress { width: min(220px, 70%); }
.mri-overlay__pct {
  font-family: var(--font-mono);
  font-size: 0.78rem;
  color: var(--text-tertiary);
}
.mri-error { max-width: min(440px, 86%); }
.mri-error__title {
  margin: 0 0 4px;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}
.mri-error p {
  margin: 2px 0;
  color: var(--text-secondary);
}
`;

/**
 * SagittalMRIViewer component
 * Displays a sagittal MRI view with IVD centroid overlays by directly accessing MHA files
 *
 * Props:
 * - selectedLevel: Currently selected IVD level (e.g., "L4-L5")
 * - onCentroidClick: Callback when a centroid is clicked
 * - currentAxialSlice: Current axial slice index for crosshair display
 * - onSliceChange: Callback when sagittal slice changes (sliceIndex)
 */
function SagittalMRIViewer({ selectedLevel, onCentroidClick, currentAxialSlice, onSliceChange }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlice, setCurrentSlice] = useState(0);
  const [totalSlices, setTotalSlices] = useState(0);
  const [imageData, setImageData] = useState(null);
  const [centroids, setCentroids] = useState({});
  const [mriInfo, setMriInfo] = useState(null);
  const [isPreloading, setIsPreloading] = useState(true);
  const [preloadProgress, setPreloadProgress] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [crosshairLine, setCrosshairLine] = useState(null);
  const [imageDimensions, setImageDimensions] = useState(null);
  const [crosshairCache, setCrosshairCache] = useState(null); // Cached crosshairs for all axial slices
  const [ivdCrosshairs, setIvdCrosshairs] = useState({}); // IVD centroid slice lines (green lines)

  const containerRef = useRef(null);
  const imageRef = useRef(null);

  // Initialize MRI cache and load volume info
  useEffect(() => {
    const initializeMRI = async () => {
      try {
        setIsPreloading(true);
        console.log('Initializing MRI viewer...');

        // Clear any existing cache when initializing with new data
        // sagittalMriCache.clear();

        // Load volume info
        const volumeInfo = await sagittalMriCache.loadVolumeInfo();

        if (volumeInfo && volumeInfo.sagittal && volumeInfo.sagittal.available) {
          setMriInfo(volumeInfo.sagittal);

          // If dimensions are available, use them to set total slices
          if (volumeInfo.sagittal.dimensions) {
            const sliceCount = volumeInfo.sagittal.dimensions[0];
            setTotalSlices(sliceCount);

            // Set a good initial slice (middle of the volume)
            const middleSlice = Math.floor(sliceCount / 2);
            setCurrentSlice(middleSlice);

            // Start preloading all slices for smooth performance
            console.log(`Starting to preload ${sliceCount} MRI slices...`);
            await sagittalMriCache.preloadAllSlices(sliceCount, (progress) => {
              setPreloadProgress(progress);
            });

            console.log('MRI preloading complete');

            // Check for cached crosshair data first
            const cachedAuxData = sagittalMriCache.getAuxiliaryData();

            if (cachedAuxData) {
              console.log('Using cached crosshair data');
              setCrosshairCache(cachedAuxData.crosshairs);
              if (cachedAuxData.ivd_crosshairs) {
                setIvdCrosshairs(cachedAuxData.ivd_crosshairs);
              }
            } else {
              // Precompute and cache all crosshair lines for instant lookup
              try {
                console.log('Precomputing crosshair lines for all axial slices...');
                const crosshairResponse = await api.get(`${API_URL}/mri/crosshair/precompute`);
                if (crosshairResponse.data.status === 'success') {
                  const data = crosshairResponse.data;
                  setCrosshairCache(data.crosshairs);
                  console.log(`Cached ${Object.keys(data.crosshairs).length} crosshair lines`);

                  // Also store IVD centroid slice lines (for green lines showing true centroid positions)
                  let ivdCrosshairsData = {};
                  if (data.ivd_crosshairs) {
                    ivdCrosshairsData = data.ivd_crosshairs;
                    setIvdCrosshairs(ivdCrosshairsData);
                    console.log(`Cached ${Object.keys(ivdCrosshairsData).length} IVD centroid lines`);
                  }

                  // Cache the data for future page loads
                  sagittalMriCache.setAuxiliaryData({
                    crosshairs: data.crosshairs,
                    ivd_crosshairs: ivdCrosshairsData
                  });
                }
              } catch (err) {
                console.warn('Failed to precompute crosshairs:', err);
              }
            }

            setIsPreloading(false);
          }

          console.log('MRI info loaded:', volumeInfo.sagittal);
        } else {
          setError('No sagittal MRI data available');
          setIsPreloading(false);
        }
      } catch (error) {
        console.error('Error initializing MRI:', error);
        setError(`Error initializing MRI: ${error.message}`);
        setIsPreloading(false);
      }
    };

    initializeMRI();
  }, []);

  // Average positions are now loaded with volume info in initializeMRI

  // Update slice data when current slice changes
  useEffect(() => {
    const updateSliceData = async () => {
      if (!mriInfo || !mriInfo.available || isPreloading) return;

      setLoading(true);
      setError(null);
      setImageLoaded(false); // Reset image loaded state

      try {
        // Try to get from cache first
        let sliceData = sagittalMriCache.getSlice(currentSlice, true);

        if (!sliceData) {
          // If not cached, fetch it
          if (DEBUG) console.log(`Fetching MRI slice ${currentSlice}...`);
          sliceData = await sagittalMriCache.fetchSlice(currentSlice, true);
        } else {
          if (DEBUG) console.log(`Using cached MRI slice ${currentSlice}`);
        }

        if (sliceData) {
          // Set the image data
          if (DEBUG) {
            console.log('Setting image data with dimensions:', sliceData.dimensions);
            console.log('Image aspect ratio:', sliceData.dimensions[1] / sliceData.dimensions[0]);
          }
          setImageData({
            url: sliceData.imageUrl,
            dimensions: sliceData.dimensions
          });

          // Set centroids for this slice
          setCentroids(sliceData.centroids || {});

          // Update total slices if different
          if (sliceData.totalSlices && sliceData.totalSlices !== totalSlices) {
            setTotalSlices(sliceData.totalSlices);
          }

          // Preload nearby slices in background
          sagittalMriCache.preloadSlices(currentSlice, totalSlices, 10);
        } else {
          setError('No data received for slice');
        }
      } catch (error) {
        console.error(`Error loading slice ${currentSlice}:`, error);
        setError(`Error loading slice: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    updateSliceData();
  }, [currentSlice, mriInfo, totalSlices, isPreloading]);

  // Notify parent when slice changes
  useEffect(() => {
    if (onSliceChange && !isPreloading) {
      onSliceChange(currentSlice);
    }
  }, [currentSlice, onSliceChange, isPreloading]);

  // Update crosshair line from cache when axial slice changes (instant lookup, no network call)
  useEffect(() => {
    if (currentAxialSlice === undefined || currentAxialSlice === null) {
      setCrosshairLine(null);
      return;
    }

    if (crosshairCache) {
      // Instant lookup from precomputed cache
      const crosshair = crosshairCache[String(currentAxialSlice)];
      if (crosshair) {
        setCrosshairLine(crosshair);
        if (DEBUG) console.log('Crosshair line (from cache):', crosshair);
      } else {
        setCrosshairLine(null);
      }
    } else {
      // Cache not yet loaded, crosshair won't show until it is
      setCrosshairLine(null);
    }
  }, [currentAxialSlice, crosshairCache]);

  // Handle slice slider change
  const handleSliderChange = (e) => {
    if (!isPreloading) {
      setCurrentSlice(parseInt(e.target.value));
    }
  };

  // Handle mouse wheel for smooth scrolling
  const handleWheel = useCallback((e) => {
    e.preventDefault();

    // Disable scrolling while preloading
    if (isPreloading) return;

    // Use larger steps for faster scrolling with shift key
    const step = e.shiftKey ? 5 : 1;
    const delta = e.deltaY > 0 ? step : -step;

    setCurrentSlice(prev => {
      const newSlice = prev + delta;
      return Math.max(0, Math.min(totalSlices - 1, newSlice));
    });
  }, [totalSlices, isPreloading]);

  // Add wheel event listener
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        container.removeEventListener('wheel', handleWheel);
      };
    }
  }, [handleWheel]);

  // Handle next/previous slice navigation
  const navigateSlice = (delta) => {
    const newSlice = Math.max(0, Math.min(totalSlices - 1, currentSlice + delta));
    setCurrentSlice(newSlice);
  };

  // Handle centroid click
  const handleCentroidClick = (region) => {
    if (onCentroidClick) {
      // Extract disc level from region name, e.g. "IVD_L4/L5" → "L4-L5"
      const match = region.match(/IVD_([A-Z]\d+)\/([A-Z]\d+)/);
      if (match) {
        const discLevel = `${match[1]}-${match[2]}`;
        onCentroidClick(discLevel);
      } else {
        onCentroidClick(region);
      }
    }
  };

  if (error) {
    return (
      <div className="viewer mri-viewer mri-viewer--center">
        <style>{viewerCss}</style>
        <div className="alert alert--danger mri-error">
          <div>
            <h3 className="mri-error__title">Error Loading MRI</h3>
            <p>{error}</p>
            <p>Make sure the backend server is running at {API_URL}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="viewer mri-viewer" ref={containerRef}>
      <style>{viewerCss}</style>
      {isPreloading && (
        <div className="mri-overlay">
          <div className="spinner"></div>
          <p>Loading MRI data...</p>
          <div className="progress mri-overlay__progress">
            <div
              className="progress__fill"
              style={{ width: `${preloadProgress}%` }}
            />
          </div>
          <p className="mri-overlay__pct">{Math.round(preloadProgress)}%</p>
        </div>
      )}

      {loading && !imageData && !isPreloading && (
        <div className="mri-overlay">
          <div className="spinner"></div>
          <p>Loading slice...</p>
        </div>
      )}

      <div className="mri-viewer__canvas">
        {/* Text Overlay - positioned at top of canvas container */}
        <div className="viewer__hud mri-hud mri-hud--tl">
          <span>Sagittal MRI — Slice</span>
          <span className="mri-hud__value">{currentSlice + 1}/{totalSlices}</span>
        </div>

        <div className="mri-image-wrap">
          {imageData && (
            <>
              <img
                ref={imageRef}
                src={imageData.url}
                alt="Sagittal MRI"
                className="mri-image"
                onError={() => {
                  setError('Failed to load sagittal MRI image');
                }}
                onLoad={(e) => {
                  // Store dimensions and mark as loaded
                  const img = e.target;
                  setImageDimensions({
                    offsetWidth: img.offsetWidth,
                    offsetHeight: img.offsetHeight
                  });
                  setImageLoaded(true);
                }}
              />

              {/* Overlay for centroids and horizontal lines - positioned exactly on the image */}
              {imageDimensions && imageLoaded && (() => {
                // Calculate actual rendered image dimensions (accounting for objectFit: contain)
                const containerWidth = imageDimensions.offsetWidth;
                const containerHeight = imageDimensions.offsetHeight;
                const imgNaturalWidth = imageData.dimensions[1];
                const imgNaturalHeight = imageData.dimensions[0];

                const containerRatio = containerWidth / containerHeight;
                const imageRatio = imgNaturalWidth / imgNaturalHeight;

                let renderedWidth, renderedHeight, offsetX, offsetY;

                if (containerRatio > imageRatio) {
                  // Container is wider - image is constrained by height
                  renderedHeight = containerHeight;
                  renderedWidth = containerHeight * imageRatio;
                  offsetX = (containerWidth - renderedWidth) / 2;
                  offsetY = 0;
                } else {
                  // Container is taller - image is constrained by width
                  renderedWidth = containerWidth;
                  renderedHeight = containerWidth / imageRatio;
                  offsetX = 0;
                  offsetY = (containerHeight - renderedHeight) / 2;
                }

                return (
                  <div style={{
                    position: 'absolute',
                    top: `${offsetY}px`,
                    left: `${offsetX}px`,
                    width: `${renderedWidth}px`,
                    height: `${renderedHeight}px`,
                    pointerEvents: 'none'
                  }}>
                    {/* SVG overlay that scales with the image */}
                    <svg
                      viewBox={`0 0 ${imgNaturalWidth} ${imgNaturalHeight}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0
                      }}
                      preserveAspectRatio="none"
                    >
                      {/* Centroids - styled with theme colors */}
                      {Object.entries(centroids).map(([region, coords]) => {
                        const x = coords[0];
                        const y = coords[1];
                        // Check if this centroid's IVD is on the current axial slice
                        const ivdData = ivdCrosshairs[region];
                        const isOnCurrentSlice = ivdData && ivdData.axial_slice === currentAxialSlice;

                        return (
                          <g key={`centroid-${region}`} style={{ cursor: 'pointer', pointerEvents: 'auto' }} onClick={() => handleCentroidClick(region)}>
                            {/* Outer glow for better visibility */}
                            <circle cx={x} cy={y} r="6" fill="none" stroke="rgba(0, 0, 0, 0.5)" strokeWidth="2" />
                            {/* Main dot - teal when on current slice, primary blue otherwise */}
                            <circle
                              cx={x}
                              cy={y}
                              r="4"
                              fill={isOnCurrentSlice ? theme.colors.secondary.main : theme.colors.primary.main}
                              stroke={isOnCurrentSlice ? theme.colors.secondary.light : theme.colors.primary.light}
                              strokeWidth="1.5"
                            />
                            {/* Label */}
                            <text
                              x={x + 8}
                              y={y + 4}
                              fill={theme.colors.neutral.text.primary}
                              fontSize="10"
                              fontFamily="Arial, sans-serif"
                              style={{ textShadow: '1px 1px 2px black, -1px -1px 2px black' }}
                            >
                              {region}
                            </text>
                          </g>
                        );
                      })}
                      {/* Crosshair line - changes color based on whether it's on an IVD slice */}
                      {crosshairLine && (() => {
                        // Check if current axial slice is an IVD slice
                        const isOnIvdSlice = Object.values(ivdCrosshairs).some(
                          data => data.axial_slice === currentAxialSlice
                        );
                        return (
                          <line
                            x1={crosshairLine.start.x}
                            y1={crosshairLine.start.y}
                            x2={crosshairLine.end.x}
                            y2={crosshairLine.end.y}
                            stroke={isOnIvdSlice ? theme.colors.secondary.main : theme.colors.error.main}
                            strokeWidth="2"
                          />
                        );
                      })()}
                    </svg>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      </div>

      {/* Slice Navigation Controls */}
      <div className="mri-viewer__controls">
        <button
          type="button"
          className="btn btn--secondary mri-nav-btn"
          disabled={currentSlice <= 0 || isPreloading}
          onClick={() => navigateSlice(-1)}
        >
          &lt;
        </button>

        <div className="mri-slider-wrap">
          <input
            type="range"
            min="0"
            max={totalSlices - 1}
            value={currentSlice}
            onChange={handleSliderChange}
            className="mri-slider"
            disabled={isPreloading}
          />
        </div>

        <button
          type="button"
          className="btn btn--secondary mri-nav-btn"
          disabled={currentSlice >= totalSlices - 1 || isPreloading}
          onClick={() => navigateSlice(1)}
        >
          &gt;
        </button>
      </div>
    </div>
  );
}

export default SagittalMRIViewer;
